import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Users,
  UserCheck,
  UserX,
  Calendar,
  Search,
  LogIn,
  Loader2,
} from "lucide-react";
import { API_BASE_URL } from "../../config";
import { formatDateWithTime } from "../utils/formatDate";
import { useTheme } from "../../contexts/ThemeContext.jsx";
import {
  DEFAULT_ROWS_PER_PAGE,
  TABLE_FIXED_HEIGHT_PX,
} from "../shared/tableConfig";
import GlobalDataTable from "../shared/GlobalDataTable.jsx";

const UserTable = () => {
  const { theme } = useTheme();
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    recentUsers: 0,
    demoAccounts: 0,
    inactiveAccounts: 0,
    activeAccounts: 0,
  });
  const [filterType, setFilterType] = useState(null); // Filter by account type or recent users
  const [impersonatingUserId, setImpersonatingUserId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const token = window.__appSession.getToken();
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/admin/users`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (Array.isArray(data.users)) {
          setUsers(data.users);
          calculateStats(data.users);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const calculateStats = (usersData) => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const recentUsers = usersData.filter(
      (user) => user.createdAt && new Date(user.createdAt) >= thirtyDaysAgo,
    ).length;

    const demoAccounts = usersData.filter(
      (user) => user.accountType === "No data" && !user.apiKey,
    ).length;

    const inactiveAccounts = usersData.filter(
      (user) => user.accountType === "No data" && user.apiKey,
    ).length;

    const activeAccounts = usersData.filter(
      (user) => user.accountType === "Regular" && user.apiKey,
    ).length;

    setStats({
      recentUsers,
      demoAccounts,
      inactiveAccounts,
      activeAccounts,
    });
  };

  const handleStatusChange = async (userId, newStatus) => {
    const token = window.__appSession.getToken();
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/auth/updateAccountStatus`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, status: newStatus }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update user status");
      }

      const updatedUsers = users.map((user) =>
        user._id === userId ? { ...user, accountStatus: newStatus } : user,
      );
      setUsers(updatedUsers);
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleImpersonate = async (user) => {
    const token = window.__appSession.getToken();
    setImpersonatingUserId(user._id);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/auth/admin/impersonate/${user._id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to create impersonation session");
      }

      const data = await response.json();
      const redirectPath = data.impersonatedUser?.isAdmin ? "/admin" : "/whatsappdashboard";
      const opened = window.__appSession.openImpersonationWindow(
        {
          token: data.token,
          expiresAt: data.expiresAt || "",
          impersonatedUser: data.impersonatedUser || {},
          impersonator: data.impersonator || {},
        },
        redirectPath,
      );

      if (!opened) {
        window.__appSession.startImpersonationSession({
          token: data.token,
          expiresAt: data.expiresAt || "",
          impersonatedUser: data.impersonatedUser || {},
          impersonator: data.impersonator || {},
        });
        window.location.assign(redirectPath);
      }
    } catch (error) {
      console.error("Error creating impersonation session:", error);
    } finally {
      setImpersonatingUserId(null);
    }
  };

  const getAccountType = (user) => {
    if (user.accountType === "No data" && !user.apiKey) {
      return "Demo";
    } else if (user.accountType === "No data" && user.apiKey) {
      return "In-Active";
    } else if (user.accountType === "Regular" && user.apiKey) {
      return "Active";
    }
    return "Unknown";
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      (user.username || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.phone || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email || "").toLowerCase().includes(searchTerm.toLowerCase());

    if (!filterType) return matchesSearch;

    if (filterType === "Recent") {
      const thirtyDaysAgo = new Date(
        new Date().getTime() - 30 * 24 * 60 * 60 * 1000,
      );
      return (
        matchesSearch &&
        user.createdAt &&
        new Date(user.createdAt) >= thirtyDaysAgo
      );
    } else if (filterType === "Demo") {
      return matchesSearch && user.accountType === "No data" && !user.apiKey;
    } else if (filterType === "In-Active") {
      return matchesSearch && user.accountType === "No data" && user.apiKey;
    } else if (filterType === "Active") {
      return matchesSearch && user.accountType === "Regular" && user.apiKey;
    }
    return matchesSearch;
  });

  const sortedUsers = filteredUsers.sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);

    if (isNaN(dateA)) return 1;
    if (isNaN(dateB)) return -1;

    return dateB - dateA;
  });

  const paginatedUsers = sortedUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );
  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / rowsPerPage));

  const getAccountTypeStyle = (accountType) => {
    if (accountType === "Demo") {
      return "border border-violet-200 bg-violet-100 text-violet-700";
    }
    if (accountType === "Active") {
      return "border border-emerald-200 bg-emerald-100 text-emerald-700";
    }
    if (accountType === "In-Active") {
      return "border border-rose-200 bg-rose-100 text-rose-700";
    }
    return "border border-slate-200 bg-slate-100 text-slate-700";
  };

  const statsCards = [
    {
      title: "Users Registered",
      value: stats.recentUsers,
      icon: Calendar,
      color: "from-indigo-600 to-violet-600",
      bgColor: "from-indigo-50 via-indigo-50 to-violet-100",
      textColor: "text-indigo-700",
      filterType: "Recent",
    },
    {
      title: "Demo",
      value: stats.demoAccounts,
      icon: Users,
      color: "from-violet-600 to-fuchsia-600",
      bgColor: "from-violet-50 via-violet-50 to-fuchsia-100",
      textColor: "text-violet-700",
      filterType: "Demo",
    },
    {
      title: "In-Active",
      value: stats.inactiveAccounts,
      icon: UserX,
      color: "from-rose-600 to-red-600",
      bgColor: "from-rose-50 via-rose-50 to-red-100",
      textColor: "text-rose-700",
      filterType: "In-Active",
    },
    {
      title: "Active",
      value: stats.activeAccounts,
      icon: UserCheck,
      color: "from-emerald-600 to-cyan-600",
      bgColor: "from-emerald-50 via-emerald-50 to-cyan-100",
      textColor: "text-emerald-700",
      filterType: "Active",
    },
  ];

  return (
    <div className="space-y-2">
      <div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-2">
          {statsCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.title}
                className={`cursor-pointer rounded-xl border bg-gradient-to-br ${stat.bgColor} p-2 shadow-sm transition-all ${
                  filterType === stat.filterType
                    ? "border-indigo-500 shadow-[0_16px_30px_-20px_rgba(79,70,229,0.85)]"
                    : "border-indigo-200/70"
                }`}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                onClick={() => {
                  setFilterType((prev) =>
                    prev === stat.filterType ? null : stat.filterType,
                  );
                  setPage(0);
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-600">
                      {stat.title}
                    </p>
                    <p className={`text-xl font-bold ${stat.textColor} mt-1`}>
                      {stat.value}
                    </p>
                  </div>
                  <div className={`rounded-lg bg-gradient-to-br p-2 ${stat.color}`}>
                    <Icon className="h-4 w-4 text-white" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <GlobalDataTable
            columns={[
              {
                key: "index",
                header: "#",
                render: (_, index) => rowsPerPage * page + index + 1,
              },
              {
                key: "createdAt",
                header: "CreatedAt",
                hideOnMobile: true,
                cellClassName: "whitespace-nowrap text-slate-700",
                render: (user) =>
                  user.createdAt ? formatDateWithTime(user.createdAt) : "No data",
              },
              {
                key: "username",
                header: "Username",
                cellClassName: "whitespace-nowrap font-medium text-slate-900",
                render: (user) => user.username,
              },
              {
                key: "name",
                header: "Name",
                cellClassName: "whitespace-nowrap text-slate-900",
                render: (user) => user.name,
              },
              {
                key: "email",
                header: "Email",
                hideOnMobile: true,
                cellClassName: "whitespace-nowrap text-slate-700",
                render: (user) => user.email,
              },
              {
                key: "phone",
                header: "Phone",
                hideOnMobile: true,
                cellClassName: "whitespace-nowrap text-slate-700",
                render: (user) => user.phone,
              },
              {
                key: "accountType",
                header: "Ac. Type",
                cellClassName: "whitespace-nowrap",
                render: (user) => (
                  <span
                    className={`inline-flex text-[10px] px-1 rounded-full ${getAccountTypeStyle(
                      getAccountType(user),
                    )}`}
                  >
                    {getAccountType(user)}
                  </span>
                ),
              },
              {
                key: "activeUpto",
                header: "Active Until",
                hideOnMobile: true,
                cellClassName: "whitespace-nowrap text-slate-700",
                render: (user) =>
                  user.activeUpto ? formatDateWithTime(user.activeUpto) : "No data",
              },
              {
                key: "apiKey",
                header: "API Key",
                hideOnMobile: true,
                cellClassName: "whitespace-nowrap text-slate-700",
                render: (user) => (
                  <span className="rounded border border-indigo-200 bg-indigo-50 px-1 py-0.5 font-mono text-[10px] text-indigo-700">
                    {user.apiKey ? `${user.apiKey}` : "No API key"}
                  </span>
                ),
              },
              {
                key: "status",
                header: "Status",
                cellClassName: "whitespace-nowrap",
                render: (user) => (
                  <select
                    value={user.accountStatus || "inactive"}
                    onChange={(e) => handleStatusChange(user._id, e.target.value)}
                    className={`rounded-lg border px-1.5 py-0.5 text-xs focus:outline-none focus:ring-2 ${theme.inputFocusRing} focus:border-transparent ${
                      user.accountStatus === "active"
                        ? "border-emerald-200 bg-emerald-100 text-emerald-700"
                        : "border-rose-200 bg-rose-100 text-rose-700"
                    }`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                ),
              },
              {
                key: "impersonate",
                header: "Impersonate",
                cellClassName: "whitespace-nowrap",
                render: (user) => (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleImpersonate(user);
                    }}
                    disabled={impersonatingUserId === user._id}
                    className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {impersonatingUserId === user._id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <LogIn className="h-3.5 w-3.5" />
                    )}
                    <span>Impersonate</span>
                  </button>
                ),
              },
            ]}
            rows={paginatedUsers}
            rowKey={(user) => user._id}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={sortedUsers.length}
            onPageChange={(nextPage) =>
              setPage(Math.min(Math.max(0, nextPage), totalPages - 1))
            }
            onRowsPerPageChange={(nextRows) => {
              setRowsPerPage(nextRows);
              setPage(0);
            }}
            loading={loading}
            fixedHeight={TABLE_FIXED_HEIGHT_PX}
            tableTitle="User Management"
            tableSubtitle="Filter, inspect, update status, and open a 30-minute impersonation session in a new tab"
            beforeTable={
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by name, phone, or email..."
                  className={`w-full rounded-lg border border-indigo-200 bg-white/90 py-2 pl-8 pr-2 text-xs text-slate-700 focus:ring-2 ${theme.inputFocusRing} focus:border-transparent`}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(0);
                  }}
                />
              </div>
            }
            mobileExpandedRow={(user) => (
              <div className="space-y-1 text-xs text-slate-700">
                <div>
                  <span className="font-semibold">Created At:</span>{" "}
                  {user.createdAt ? formatDateWithTime(user.createdAt) : "No data"}
                </div>
                <div>
                  <span className="font-semibold">Email:</span> {user.email}
                </div>
                <div>
                  <span className="font-semibold">Phone:</span> {user.phone}
                </div>
                <div>
                  <span className="font-semibold">Active Until:</span>{" "}
                  {user.activeUpto ? formatDateWithTime(user.activeUpto) : "No data"}
                </div>
                <div>
                  <span className="font-semibold">API Key:</span>{" "}
                  <span className="rounded border border-indigo-200 bg-indigo-50 px-1 py-0.5 font-mono text-[10px] text-indigo-700">
                    {user.apiKey ? `${user.apiKey}` : "No API key"}
                  </span>
                </div>
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={() => handleImpersonate(user)}
                    disabled={impersonatingUserId === user._id}
                    className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {impersonatingUserId === user._id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <LogIn className="h-3.5 w-3.5" />
                    )}
                    <span>Impersonate</span>
                  </button>
                </div>
              </div>
            )}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default UserTable;

// import React, { useEffect, useState } from 'react';
// import {
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Paper,
//   Typography,
//   TablePagination,
//   TextField,
//   Button,
//   Menu,
//   MenuItem,
// } from '@mui/material';
// import { styled } from '@mui/material/styles';
// import { API_BASE_URL } from '../../config';

// // Styled components
// const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
//   maxWidth: 1500,
//   margin: '20px auto',
//   borderRadius: '8px',
//   boxShadow: theme.shadows[5],
// }));

// const StyledTableHeader = styled(TableRow)({
//   backgroundColor: '#1976d2',
//   color: '#fff',
// });

// const StyledTableRow = styled(TableRow)(({ theme }) => ({
//   '&:hover': {
//     backgroundColor: theme.palette.action.hover,
//   },
// }));

// const Footer = () => {
//   return (
//     <footer className="bg-[#25D366] text-white py-4 text-center">
//       <p>&copy; {new Date().getFullYear()} Smart ItBox. All rights reserved.</p>
//     </footer>
//   );
// };

// const UsersTable = () => {
//   const [users, setUsers] = useState([]);
//   const [page, setPage] = useState(0);
//   const [rowsPerPage, setRowsPerPage] = useState(100);
//   const [searchTerm, setSearchTerm] = useState('');
//   const [anchorEl, setAnchorEl] = useState(null); // For dropdown
//   const [selectedUser, setSelectedUser] = useState(null); // User whose status is being updated
//   useEffect(() => {
//     const fetchData = async () => {
//       const token = window.__appSession.getToken();
//       try {
//         const response = await fetch(`${API_BASE_URL}/api/auth/admin/users`, {
//           method: 'GET',
//           headers: {
//             'Content-Type': 'application/json',
//             'Authorization': `Bearer ${token}`,
//           },
//         });

//         if (!response.ok) {
//           throw new Error(`HTTP error! status: ${response.status}`);
//         }

//         const data = await response.json();
//         if (Array.isArray(data.users)) {
//           setUsers(data.users); // Set the fetched users into state
//         }
//       } catch (error) {
//         console.error('Error fetching users:', error);
//       }
//     };

//     fetchData();
//   }, []);

//   const handleChangePage = (event, newPage) => {
//     setPage(newPage);
//   };

//   const handleChangeRowsPerPage = (event) => {
//     setRowsPerPage(parseInt(event.target.value, 10));
//     setPage(0);
//   };

//   const handleSearchChange = (event) => {
//     setSearchTerm(event.target.value.toLowerCase());
//   };

//   // Filter users based on search term
//   const filteredUsers = users.filter(user => {
//     return (
//       user.username.toLowerCase().includes(searchTerm) ||
//       user.phone.toLowerCase().includes(searchTerm)
//     );
//   });

//   // Sort the filtered users by the account creation date (most recent first)
//   const sortedUsers = filteredUsers.sort((a, b) => {
//     const dateA = new Date(a.accountCreated);
//     const dateB = new Date(b.accountCreated);

//     // Check if either date is invalid (e.g., 'No data')
//     if (isNaN(dateA)) return 1; // Move 'No data' to the bottom
//     if (isNaN(dateB)) return -1; // Move 'No data' to the bottom

//     return dateB - dateA; // Sort descending: most recent first
//   });

//   // Determine account type color
//   const getAccountTypeColor = (accountType) => {
//     if (accountType === 'Demo') {
//       return { backgroundColor: '#FFEB3B', color: '#333333' }; // Yellow for demo accounts
//     }
//     if (accountType === 'Regular') {
//       return { backgroundColor: '#4CAF50', color: 'white' }; // Green for regular accounts
//     }
//     return { backgroundColor: '#F44336', color: 'white' }; // Red for no data or others
//   };

//   const handleStatusChange = async (userId, newStatus) => {
//     const token = localStorage.getItem('authToken');
//     try {
//       const response = await fetch(`${API_BASE_URL}/api/auth/updateAccountStatus`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           'Authorization': `Bearer ${token}`,
//         },
//         body: JSON.stringify({ userId, status: newStatus }),
//       });

//       if (!response.ok) {
//         throw new Error('Failed to update user status');
//       }

//       const updatedUsers = users.map((user) =>
//         user._id === userId ? { ...user, accountStatus: newStatus } : user
//       );
//       setUsers(updatedUsers); // Update users state after status change

//     } catch (error) {
//       console.error('Error updating status:', error);
//     }
//   };

//   return (
//     <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
//       <div style={{ flex: '1', padding: '16px' }}>
//         <TextField
//           label="Search by Name or Phone"
//           variant="outlined"
//           fullWidth
//           value={searchTerm}
//           onChange={handleSearchChange}
//           style={{ marginBottom: '16px' }}
//         />
//         <StyledTableContainer component={Paper} style={{ maxHeight: '1600px', overflowY: 'auto' }}>
//           <Typography variant="h6" style={{ padding: '16px', textAlign: 'center' }}>
//             User Details
//           </Typography>
//           <Table stickyHeader>
//             <TableHead>
//               <StyledTableHeader>
//                 {/* Account Created Column */}
//                 <TableCell style={{ backgroundColor: '#1976d2', color: '#fff' }}>Account Created</TableCell>

//                 {/* Other Columns */}
//                 <TableCell style={{ backgroundColor: '#1976d2', color: '#fff' }}>Username</TableCell>
//                 <TableCell style={{ backgroundColor: '#1976d2', color: '#fff' }}>Name</TableCell>
//                 <TableCell style={{ backgroundColor: '#1976d2', color: '#fff' }}>Email</TableCell>
//                 <TableCell style={{ backgroundColor: '#1976d2', color: '#fff' }}>Phone</TableCell>
//                 <TableCell style={{ backgroundColor: '#1976d2', color: '#fff' }}>Account Type</TableCell>
//                 <TableCell style={{ backgroundColor: '#1976d2', color: '#fff' }}>Active Upto</TableCell>
//                 <TableCell style={{ backgroundColor: '#1976d2', color: '#fff' }}>API Key</TableCell>
//                 <TableCell style={{ backgroundColor: '#1976d2', color: '#fff' }}>Account Status</TableCell>
//               </StyledTableHeader>
//             </TableHead>
//             <TableBody>
//               {sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
//                 <StyledTableRow key={user._id}>
//                   {/* Account Created Column */}
//                   <TableCell>
//   {user.createdAt ?
//     new Date(user.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
//     : 'No data'
//   }
// </TableCell>

//                   {/* Other Columns */}
//                   <TableCell>{user.username}</TableCell>
//                   <TableCell>{user.name}</TableCell>
//                   <TableCell>{user.email}</TableCell>
//                   <TableCell>{user.phone}</TableCell>
//                   <TableCell style={getAccountTypeColor(user.accountType)}>
//                     {user.accountType || 'No data'}
//                   </TableCell>
//                   <TableCell>{user.activeUpto ? new Date(user.activeUpto).toLocaleString() : 'No data'}</TableCell>
//                   <TableCell>{user.apiKey ? user.apiKey : 'No API key available'}</TableCell>
//                   <TableCell>
//                     <Button
//                       variant="contained"
//                       color={user.accountStatus === 'active' ? 'success' : 'error'}
//                       onClick={(e) => {
//                         setSelectedUser(user);
//                         setAnchorEl(e.currentTarget);
//                       }}
//                     >
//                       {user.accountStatus === 'active' ? 'Active' : 'Inactive'}
//                     </Button>
//                     <Menu
//                       anchorEl={anchorEl}
//                       open={Boolean(anchorEl) && selectedUser?._id === user._id}
//                       onClose={() => setAnchorEl(null)}
//                     >
//                       <MenuItem onClick={() => handleStatusChange(user._id, 'active')}>Active</MenuItem>
//                       <MenuItem onClick={() => handleStatusChange(user._id, 'inactive')}>Inactive</MenuItem>
//                     </Menu>
//                   </TableCell>
//                 </StyledTableRow>
//               ))}
//               {sortedUsers.length === 0 && (
//                 <TableRow>
//                   <TableCell colSpan={8} style={{ textAlign: 'center' }}>
//                     No users available
//                   </TableCell>

//                 </TableRow>
//               )}
//             </TableBody>
//           </Table>
//         </StyledTableContainer>
//         <TablePagination
//           rowsPerPageOptions={[5, 10, 25]}
//           component="div"
//           count={sortedUsers.length}
//           rowsPerPage={rowsPerPage}
//           page={page}
//           onPageChange={handleChangePage}
//           onRowsPerPageChange={handleChangeRowsPerPage}
//         />
//       </div>
//       <Footer />
//     </div>
//   );
// };

// export default UsersTable;
